import React, { useState } from "react";
import axios from "axios";
import { ArrowUp, Download, Globe, History, Lock, MessageSquare, Sparkles, X } from "lucide-react";
import { DocumentSections } from "@/components/documents/DocumentSections";
import { DOC_STATUS_META, docStatusMeta, fmtDocDate } from "@/lib/docStatus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCORE_LABELS = {
  originality: "Originality",
  emotional_impact: "Emotion",
  commercial_strength: "Commercial",
  clarity: "Clarity",
  simplicity: "Simplicity",
  feasibility: "Feasibility",
  production_readiness: "Prod-ready",
  brand_consistency: "Brand",
};

function confidenceLine(c) {
  if (c >= 90) return `Confidence ${c}%. Ready to send.`;
  if (c >= 75) return `Confidence ${c}%. Strong draft, a few assumptions made.`;
  return `Confidence ${c}%. Review the assumptions before sharing.`;
}

function ShareBar({ o, projectId, onOutput }) {
  const shared = o.visibility === "client";
  const [busy, setBusy] = useState(false);
  const status = o.doc_status || "draft";

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/hq/projects/${projectId}/outputs/${o.id}/share`,
        { shared: !shared },
      );
      onOutput(data.output);
    } catch (e) {
      console.error("share toggle failed", e);
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (s) => {
    if (busy || s === status) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/hq/projects/${projectId}/outputs/${o.id}/status`,
        { status: s },
      );
      onOutput(data.output);
    } catch (e) {
      console.error("status change failed", e);
    } finally {
      setBusy(false);
    }
  };

  const [distilling, setDistilling] = useState(false);
  const makeRange = async () => {
    if (distilling) return;
    setDistilling(true);
    try {
      const { data } = await axios.post(
        `${API}/hq/projects/${projectId}/outputs/${o.id}/client-range`,
      );
      onOutput(data.output); // the modal becomes the freshly distilled range
    } catch (e) {
      console.error("client range failed", e);
    } finally {
      setDistilling(false);
    }
  };

  return (
    <div className="bg-[#fafafa] border-b border-[#eee] px-8 py-3.5 flex flex-wrap items-center gap-x-5 gap-y-2.5" data-testid="hq-share-bar">
      <button
        type="button"
        onClick={toggle}
        disabled={busy}
        className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12.5px] font-semibold border transition-colors disabled:opacity-50 ${
          shared
            ? "bg-[#2e4266] text-white border-[#2e4266]"
            : "bg-white text-ink border-[#d9d9d6] hover:border-ink"
        }`}
        data-testid="hq-share-toggle"
        data-shared={shared}
      >
        {shared ? <Globe className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
        {busy ? "One moment…" : shared ? "Shared with client · Unshare" : "Share with client"}
      </button>

      <div className="flex items-center gap-1.5" data-testid="hq-doc-status-row">
        {Object.entries(DOC_STATUS_META).map(([k, m]) => (
          <button
            key={k}
            type="button"
            onClick={() => setStatus(k)}
            disabled={busy}
            className={`rounded-full px-2.5 py-1 text-[10.5px] uppercase tracking-[0.08em] border transition-colors disabled:opacity-50 ${
              status === k ? m.light : "bg-white text-muted-ink border-[#e6e6e4] hover:border-[#bbb]"
            }`}
            data-testid={`hq-doc-status-${k}`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {o.skill === "production_budget" && (
        <button
          type="button"
          onClick={makeRange}
          disabled={distilling}
          className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[12.5px] font-semibold border bg-white text-ink border-[#d9d9d6] hover:border-ink transition-colors disabled:opacity-50"
          data-testid="hq-make-client-range"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {distilling ? "Ida is distilling\u2026" : "Create client range"}
        </button>
      )}

      {o.shared_at && (
        <span className="text-[11px] text-muted-ink" data-testid="hq-doc-shared-at">
          Shared {fmtDocDate(o.shared_at)}
          {o.updated_at && o.updated_at !== o.shared_at ? ` · Updated ${fmtDocDate(o.updated_at)}` : ""}
        </span>
      )}
    </div>
  );
}

function CommentsPanel({ o, projectId, onOutput }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const comments = o.comments || [];

  const send = async () => {
    const t = text.trim();
    if (t.length < 1 || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/hq/projects/${projectId}/outputs/${o.id}/comments`,
        { text: t },
      );
      onOutput({ ...o, comments: [...comments, data.comment] });
      setText("");
    } catch (e) {
      console.error("producer comment failed", e);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mt-12 pt-8 border-t border-[#eee]" data-testid="hq-doc-comments">
      <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-muted-ink mb-4">
        <MessageSquare className="w-3.5 h-3.5" /> Client conversation
      </div>
      {comments.length === 0 && (
        <p className="text-[13px] text-muted-ink italic">No comments yet. The client can respond directly on this document.</p>
      )}
      <div className="space-y-3">
        {comments.map((c) => (
          <div
            key={c.id}
            className={`rounded-xl px-4 py-3 border ${
              c.author === "producer" ? "bg-[#f5f7fb] border-[#e2e8f2]" : "bg-white border-[#eee]"
            }`}
            data-testid="hq-doc-comment"
            data-author={c.author}
          >
            <div className="text-[10px] uppercase tracking-[0.1em] text-muted-ink mb-1">
              {c.author === "producer" ? "You" : "Client"} · {fmtDocDate(c.created_at)}
            </div>
            <p className="text-[13.5px] leading-[1.65] text-ink">{c.text}</p>
          </div>
        ))}
      </div>
      <div className="relative mt-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={2}
          placeholder="Reply to the client on this document…"
          className="w-full rounded-xl border border-[#e0e0de] px-4 py-3 pr-12 text-[13.5px] text-ink leading-[1.6] resize-none focus:outline-none focus:border-ink"
          data-testid="hq-doc-comment-input"
        />
        <button
          type="button"
          onClick={send}
          disabled={text.trim().length < 1 || sending}
          className="absolute right-2.5 bottom-3 w-8 h-8 rounded-full bg-[#0a0a0a] text-white flex items-center justify-center disabled:opacity-30"
          data-testid="hq-doc-comment-send"
          aria-label="Send reply"
        >
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

// Universal renderer for any Ida Skill output (the one schema, everywhere).
export default function DocumentView({ output, projectId, onClose, onChanged }) {
  const [o, setO] = useState(output);
  const doc = o?.document || {};
  const status = docStatusMeta(o?.doc_status || "draft");

  const handleOutput = (fresh) => {
    if (!fresh) return;
    setO(fresh);
    onChanged?.(fresh);
  };

  const history = (o?.history || []).filter(Boolean);

  return (
    <div className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <div
        className="min-h-full flex items-start justify-center px-4 py-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full max-w-[760px] rounded-2xl bg-white text-ink shadow-2xl overflow-hidden" data-testid="ida-document">
          {/* Branded header */}
          <div className="bg-[#0a0a0a] px-8 py-5 flex items-center justify-between">
            <div>
              <span className="font-display font-bold text-white text-[13px] tracking-[0.03em]">BRAND NEW MONEY</span>
              <span className="font-serif-italic font-light text-white/80 text-[12px]"> Films</span>
              <span className="text-[#E53935] text-[11px]"> &#9679;</span>
              <span className="ml-3 text-white/40 text-[10px] uppercase tracking-[0.14em]">{o?.label}</span>
            </div>
            <div className="flex items-center gap-4">
              {projectId && (
                <a
                  href={`${API}/hq/projects/${projectId}/outputs/${o.id}/pdf`}
                  className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-[12px] transition-colors"
                  data-testid="ida-document-pdf"
                >
                  <Download className="w-3.5 h-3.5" /> PDF
                </a>
              )}
              <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="ida-document-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {projectId && <ShareBar o={o} projectId={projectId} onOutput={handleOutput} />}

          <div className="px-8 md:px-12 py-10">
            <div className="flex items-center gap-2.5 mb-4">
              <span className={`rounded-full px-2.5 py-1 text-[10px] uppercase tracking-[0.1em] border ${status.light}`} data-testid="hq-doc-status-badge">
                {status.label}
              </span>
            </div>
            <h2 className="font-display font-extrabold text-[28px] md:text-[36px] tracking-[-0.03em] leading-[1.05]">
              {doc.title || o?.title}
            </h2>
            {doc.subtitle && (
              <p className="mt-3 font-serif-italic text-muted-ink text-[16px] md:text-[18px] leading-[1.5]">{doc.subtitle}</p>
            )}

            {(doc.strategic_note || o?.verdict || typeof o?.confidence === "number" || o?.scorecard) && (
              <div className="mt-8 rounded-xl bg-[#f7f7f6] border-l-2 border-[#E53935] px-5 py-4" data-testid="ida-strategic-note">
                <div className="flex items-center justify-between gap-4 mb-1.5">
                  <div className="text-[10px] uppercase tracking-[0.14em] text-[#E53935] font-bold">Ida&rsquo;s thinking · private</div>
                  {typeof o?.confidence === "number" && (
                    <div className="text-[11.5px] text-ink font-medium" data-testid="ida-confidence">{confidenceLine(o.confidence)}</div>
                  )}
                </div>
                {doc.strategic_note && <p className="text-ink text-[13.5px] leading-[1.7] italic">{doc.strategic_note}</p>}
                {o?.verdict && (
                  <p className="mt-2.5 text-[#6b6b6b] text-[12.5px] leading-[1.65]" data-testid="ida-verdict">
                    <span className="font-semibold text-ink not-italic">Her own critique:</span> {o.verdict}
                  </p>
                )}
                {o?.scorecard && (
                  <div className="mt-3.5 grid grid-cols-4 gap-2" data-testid="ida-scorecard">
                    {Object.entries(SCORE_LABELS).map(([k, label]) =>
                      o.scorecard[k] != null ? (
                        <div key={k} className="rounded-lg bg-white border border-[#eee] px-2 py-1.5 text-center">
                          <div className="font-display font-extrabold text-[15px] leading-none">{o.scorecard[k]}</div>
                          <div className="text-[9px] uppercase tracking-[0.06em] text-muted-ink mt-1">{label}</div>
                        </div>
                      ) : null
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="mt-10">
              <DocumentSections sections={doc.sections} />
            </div>

            {history.length > 0 && (
              <div className="mt-12 pt-8 border-t border-[#eee]" data-testid="hq-doc-history">
                <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.14em] text-muted-ink mb-4">
                  <History className="w-3.5 h-3.5" /> Version history
                </div>
                <div className="space-y-2">
                  {[...history].reverse().map((h) => (
                    <div key={h.id} className="flex items-baseline justify-between gap-4 text-[12.5px]">
                      <span className="text-ink">{h.label}{h.internal ? " · private" : ""}</span>
                      <span className="text-muted-ink shrink-0">{fmtDocDate(h.created_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {projectId && (o.visibility === "client" || (o.comments || []).length > 0) && (
              <CommentsPanel o={o} projectId={projectId} onOutput={handleOutput} />
            )}

            <p className="mt-12 pt-6 border-t border-[#eee] text-[11px] text-muted-ink tracking-[0.02em]">
              Made in India. Producing worldwide. · Prepared by Ida for Brand New Money Films
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
